/**
 * LaunchControlSubpage — Pre-Launch Mode, Live Payments and Root
 * Sidelining toggles, countdown date, engine pacing pause, and emergency Refund-All-Purchases
 * sweep.
 *
 * God-admin only (the sidebar already hides it for sub-admins; backend
 * also enforces ``current_god_admin`` on the refund endpoint).
 *
 * Read/write path: piggybacks on the existing /api/admin/config endpoint
 * — no new GET introduced. The PUT only sends the keys this page owns
 * (pre_launch_mode_enabled, live_payments_enabled, root_sidelining_enabled,
 * launch_at_iso, heartbeat_pacing_ratio), so
 * SystemSubpage's other config values are untouched.
 */

import {
  Activity,
  AlertTriangle,
  Banknote,
  Calendar,
  Cpu,
  Eraser,
  Loader2,
  Pause,
  Power,
  RefreshCw,
  Rocket,
  RotateCcw,
  Save,
  ShieldAlert,
  UserCheck,
  Wallet,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  executePlatformUnwindClear,
  executePlatformUnwindRefund,
  executePlatformUnwindResetMatrix,
  fetchLaunchControl,
  fetchPlatformUnwindClearPreview,
  fetchPlatformUnwindRefundPreview,
  fetchPlatformUnwindResetMatrixPreview,
  refundAllPurchases,
  updateLaunchControl,
} from '@/api/admin';
import LaunchAtFields, { LAUNCH_AT_INVALID } from '@/components/admin/LaunchAtFields';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Switch } from '@/components/ui/switch';
import { fmtUsd } from '@/lib/money';

// Patch v15 — Hide the legacy queue-based "Refund All Purchases" button.
// The new Platform Unwind subpanel (below) implements a financially safer
// refund driven by net deposit ledger sums, plus a separate withdrawal-
// balance clear. Flip this flag back to true ONLY if you need to revive
// the legacy emergency button. Backend route stays reachable for direct
// API call by a trained operator.
const SHOW_LEGACY_REFUND_BUTTON = false;

// ---------------------------------------------------------------------------
// Helpers — datetime-local rendering. The HTML datetime-local input has no
// timezone; we treat its value as wall-clock time in America/New_York and
// convert to/from a UTC ISO string at the I/O boundary so the operator
// types "2026-07-04 20:00" and the API receives a TZ-aware ISO string.
// ---------------------------------------------------------------------------
const _easternFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/New_York',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

const _easternDisplayFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/New_York',
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  timeZoneName: 'short',
});

/** Convert an ISO string (any TZ) -> a "YYYY-MM-DDTHH:MM" string in EST/EDT. */
const isoToEasternInput = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const parts = _easternFormatter.formatToParts(d).reduce((acc, p) => {
      acc[p.type] = p.value;
      return acc;
    }, {});
    return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
  } catch (_e) {
    return '';
  }
};

/** "YYYY-MM-DDTHH:MM" (Eastern wall-clock) -> TZ-aware ISO string. */
const easternInputToIso = (value) => {
  if (!value) return null;
  // Build a tentative UTC date from the wall-clock parts, then compute
  // the EST/EDT offset for that moment so the final ISO string targets
  // the correct UTC instant.
  const m = String(value).match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
  if (!m) return null;
  const [, y, mo, d, hh, mm] = m.map((x) => x);
  // First approximation: pretend it's UTC.
  const approx = new Date(Date.UTC(+y, +mo - 1, +d, +hh, +mm, 0));
  if (Number.isNaN(approx.getTime())) return null;
  // Calculate the actual EST/EDT offset at that moment.
  const tzParts = _easternFormatter.formatToParts(approx).reduce((acc, p) => {
    acc[p.type] = p.value;
    return acc;
  }, {});
  const tzWallMs = Date.UTC(+tzParts.year, +tzParts.month - 1, +tzParts.day, +tzParts.hour, +tzParts.minute, 0);
  const offsetMs = approx.getTime() - tzWallMs; // approx - what EST sees = +offset
  const realUtcMs = approx.getTime() + offsetMs;
  return new Date(realUtcMs).toISOString();
};

const easternDisplay = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return _easternDisplayFormatter.format(d);
  } catch (_e) {
    return '';
  }
};

// Pacing ratio bounds: 0 = paused, 1 = max. Spec default is 0.10.
const PACING_MIN = 0;
const PACING_MAX = 1;

const _pacingLabel = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  if (v <= 0) return 'PAUSED';
  return `${(v * 100).toFixed(0)}%`;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const LaunchControlSubpage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refunding, setRefunding] = useState(false);
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [lastRefundResult, setLastRefundResult] = useState(null);

  // Server-authoritative snapshot.
  const [server, setServer] = useState({
    pre_launch_mode_enabled: false,
    live_payments_enabled: false,
    root_sidelining_enabled: false,
    launch_at_iso: null,
    heartbeat_pacing_ratio: '0.10',
    root_sidelining_eligible_members: null,
  });

  // Edit form.
  const [form, setForm] = useState({
    pre_launch_mode_enabled: false,
    live_payments_enabled: false,
    root_sidelining_enabled: false,
    launch_at_input: '', // datetime-local string in Eastern wall-clock
    heartbeat_pacing_ratio: '0.10',
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchLaunchControl();
      const snap = {
        pre_launch_mode_enabled: !!data.pre_launch_mode_enabled,
        live_payments_enabled: !!data.live_payments_enabled,
        root_sidelining_enabled: !!data.root_sidelining_enabled,
        launch_at_iso: data.launch_at_iso || null,
        heartbeat_pacing_ratio: String(data.heartbeat_pacing_ratio ?? '0.10'),
        root_sidelining_eligible_members: eligibleCount(data.root_sidelining_eligible_members),
      };
      setServer(snap);
      setForm({
        pre_launch_mode_enabled: snap.pre_launch_mode_enabled,
        live_payments_enabled: snap.live_payments_enabled,
        root_sidelining_enabled: snap.root_sidelining_enabled,
        launch_at_input: isoToEasternInput(snap.launch_at_iso),
        heartbeat_pacing_ratio: snap.heartbeat_pacing_ratio,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Failed to load Launch Control config');
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial fetch on mount. ``load`` is also called after Save so the
  // surface UI stays authoritative. ``load`` only touches state setters,
  // so its identity is stable and this still runs once on mount.
  useEffect(() => {
    load();
  }, [load]);

  const dirty = useMemo(() => {
    const formIso = easternInputToIso(form.launch_at_input);
    // Compare ISO instants for equality, treating empty input as null.
    const serverInstant = server.launch_at_iso ? new Date(server.launch_at_iso).getTime() : null;
    const formInstant = formIso ? new Date(formIso).getTime() : null;
    return (
      Boolean(form.pre_launch_mode_enabled) !== Boolean(server.pre_launch_mode_enabled) ||
      Boolean(form.live_payments_enabled) !== Boolean(server.live_payments_enabled) ||
      Boolean(form.root_sidelining_enabled) !== Boolean(server.root_sidelining_enabled) ||
      String(form.heartbeat_pacing_ratio) !== String(server.heartbeat_pacing_ratio) ||
      serverInstant !== formInstant
    );
  }, [form, server]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const pacing = String(form.heartbeat_pacing_ratio).trim();
      const pacingNum = Number(pacing);
      if (!Number.isFinite(pacingNum) || pacingNum < PACING_MIN || pacingNum > PACING_MAX) {
        toast.error('Heartbeat pacing ratio must be between 0 and 1.');
        setSaving(false);
        return;
      }
      const launchIso = form.launch_at_input ? easternInputToIso(form.launch_at_input) : null;
      if (form.launch_at_input && !launchIso) {
        toast.error('Enter a complete launch date and time, or clear every box.');
        setSaving(false);
        return;
      }
      const data = await updateLaunchControl({
        preLaunchEnabled: !!form.pre_launch_mode_enabled,
        livePaymentsEnabled: !!form.live_payments_enabled,
        rootSideliningEnabled: !!form.root_sidelining_enabled,
        launchAtIso: launchIso,
        heartbeatPacingRatio: pacing,
      });
      const cfg = data?.config || {};
      const snap = {
        pre_launch_mode_enabled: !!cfg.pre_launch_mode_enabled,
        live_payments_enabled: !!cfg.live_payments_enabled,
        root_sidelining_enabled: !!cfg.root_sidelining_enabled,
        launch_at_iso: cfg.launch_at_iso || null,
        heartbeat_pacing_ratio: String(cfg.heartbeat_pacing_ratio ?? '0.10'),
        root_sidelining_eligible_members: eligibleCount(cfg.root_sidelining_eligible_members),
      };
      setServer(snap);
      setForm({
        pre_launch_mode_enabled: snap.pre_launch_mode_enabled,
        live_payments_enabled: snap.live_payments_enabled,
        root_sidelining_enabled: snap.root_sidelining_enabled,
        launch_at_input: isoToEasternInput(snap.launch_at_iso),
        heartbeat_pacing_ratio: snap.heartbeat_pacing_ratio,
      });
      toast.success('Launch Control updated.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Failed to save Launch Control');
    } finally {
      setSaving(false);
    }
  };

  const handleResetForm = () => {
    setForm({
      pre_launch_mode_enabled: server.pre_launch_mode_enabled,
      live_payments_enabled: server.live_payments_enabled,
      root_sidelining_enabled: server.root_sidelining_enabled,
      launch_at_input: isoToEasternInput(server.launch_at_iso),
      heartbeat_pacing_ratio: server.heartbeat_pacing_ratio,
    });
  };

  const handleRefund = async () => {
    setRefunding(true);
    try {
      const data = await refundAllPurchases('REFUND ALL');
      setLastRefundResult(data);
      toast.success(
        `Refund complete: ${data.users_affected} users, ${fmtUsd(data.refunded_subpack_total_usd)} subpacks + ${fmtUsd(data.refunded_position_total_usd)} positions`,
      );
      setRefundDialogOpen(false);
      setConfirmText('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Refund failed');
    } finally {
      setRefunding(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="launch-control-loading">
        <Loader2 className="w-6 h-6 animate-spin text-[#FFC20E]" />
      </div>
    );
  }

  const pacingNum = Number(server.heartbeat_pacing_ratio);
  const enginePaused = Number.isFinite(pacingNum) && pacingNum <= 0;

  return (
    <div className="space-y-6 max-w-[920px]" data-testid="launch-control-subpage">
      {/* --- Header ------------------------------------------------------- */}
      <div className="flex items-start gap-4">
        <div
          className="inline-flex items-center justify-center w-11 h-11 rounded-xl"
          style={{
            background: 'rgba(255,182,18,0.12)',
            border: '1px solid rgba(255,182,18,0.5)',
          }}
        >
          <Rocket className="w-5 h-5 text-[#FFC20E]" />
        </div>
        <div>
          <h2 className="font-display text-xl md:text-2xl font-bold" data-testid="launch-control-title">
            Launch Control
          </h2>
          <p className="text-[12.5px] mt-1 leading-relaxed max-w-[640px]" style={{ color: 'var(--sc-text-mute)' }}>
            Pre-launch toggle, live payments, root sidelining, countdown timer, engine pause, and emergency refund sweep. Pre-Launch Mode blocks purchases only;
            Live Payments turns real crypto deposits, withdrawals and payouts on or off.
          </p>
        </div>
      </div>

      {/* --- Live status banner ----------------------------------------- */}
      <div className="sc-glass rounded-2xl p-5 md:p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="launch-control-status-banner">
        <StatusTile
          icon={server.pre_launch_mode_enabled ? Pause : Power}
          label="Pre-Launch Mode"
          value={server.pre_launch_mode_enabled ? 'ENABLED' : 'Disabled'}
          tone={server.pre_launch_mode_enabled ? 'warn' : 'ok'}
          testId="status-pre-launch"
        />
        <StatusTile
          icon={Wallet}
          label="Live Payments"
          value={server.live_payments_enabled ? 'ON' : 'Off'}
          tone={server.live_payments_enabled ? 'ok' : 'warn'}
          testId="status-live-payments"
        />
        <StatusTile
          icon={UserCheck}
          label="Root Sidelining"
          value={server.root_sidelining_enabled ? 'ON' : 'Off'}
          tone={server.root_sidelining_enabled ? 'ok' : 'neutral'}
          testId="status-root-sidelining"
        />
        <StatusTile
          icon={Calendar}
          label="Launch (EST)"
          value={server.launch_at_iso ? easternDisplay(server.launch_at_iso) : 'Not set'}
          tone="neutral"
          testId="status-launch-at"
        />
        <StatusTile
          icon={Activity}
          label="Engine Pacing"
          value={_pacingLabel(server.heartbeat_pacing_ratio)}
          tone={enginePaused ? 'warn' : 'ok'}
          testId="status-pacing"
        />
      </div>

      {/* --- Edit form -------------------------------------------------- */}
      <div className="sc-glass rounded-2xl p-5 md:p-6 space-y-5" data-testid="launch-control-form">
        {/* Toggle */}
        <div className="flex items-start justify-between gap-4 pb-4" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div>
            <div className="font-display text-[15px] font-bold flex items-center gap-2">
              <Pause className="w-4 h-4 text-[#FFC20E]" />
              Pre-Launch Mode
            </div>
            <p className="text-[12px] mt-1 leading-relaxed" style={{ color: 'var(--sc-text-mute)' }}>
              When ON, Sub Pack purchases, Single Position purchases, and PIF are blocked with a 423 response. Deposits and withdrawals follow Live Payments
              below. The homepage shows the countdown timer below.
            </p>
          </div>
          <Switch
            checked={!!form.pre_launch_mode_enabled}
            onCheckedChange={(v) => setForm((f) => ({ ...f, pre_launch_mode_enabled: !!v }))}
            data-testid="pre-launch-toggle"
          />
        </div>

        {/* Live payments */}
        <div className="flex items-start justify-between gap-4 pb-4" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div>
            <div className="font-display text-[15px] font-bold flex items-center gap-2">
              <Wallet className="w-4 h-4 text-[#FFC20E]" />
              Live Payments
            </div>
            <p className="text-[12px] mt-1 leading-relaxed" style={{ color: 'var(--sc-text-mute)' }}>
              When ON, members can create crypto deposits and request withdrawals, and payouts can be sent. When OFF, new deposits and withdrawals are refused;
              payment notices for deposits already sent are still credited. Real money moves while this is ON.
            </p>
          </div>
          <Switch
            checked={!!form.live_payments_enabled}
            onCheckedChange={(v) => setForm((f) => ({ ...f, live_payments_enabled: !!v }))}
            data-testid="live-payments-toggle"
          />
        </div>

        {/* Root sidelining */}
        <div className="flex items-start justify-between gap-4 pb-4" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div>
            <div className="font-display text-[15px] font-bold flex items-center gap-2">
              <UserCheck className="w-4 h-4 text-[#FFC20E]" />
              Root Sidelining
            </div>
            <p className="text-[12px] mt-1 leading-relaxed" style={{ color: 'var(--sc-text-mute)' }}>
              When ON, the engine only cycles roots owned by members who hold an active 1-Year Sub Pack. Every other root stays in the root pool with its place
              kept and is skipped until its owner buys a 1-Year Sub Pack or this is turned OFF. Fillers are not affected. If no member with an active 1-Year Sub
              Pack has a root queued, nothing cycles.
            </p>
            <p className="text-[12px] mt-1.5 font-semibold" style={{ color: 'var(--sc-text-dim)' }} data-testid="root-sidelining-eligible">
              {server.root_sidelining_eligible_members == null
                ? 'Members with an active 1-Year Sub Pack: unavailable'
                : `Members with an active 1-Year Sub Pack: ${server.root_sidelining_eligible_members.toLocaleString()}`}
            </p>
          </div>
          <Switch
            checked={!!form.root_sidelining_enabled}
            onCheckedChange={(v) => setForm((f) => ({ ...f, root_sidelining_enabled: !!v }))}
            data-testid="root-sidelining-toggle"
          />
        </div>

        {/* Launch date */}
        <div>
          <label
            htmlFor="launch-at-input-month"
            className="block text-[12px] font-bold uppercase tracking-[0.16em] mb-2"
            style={{ color: 'var(--sc-text-mute)' }}
          >
            Launch Date &amp; Time (Eastern)
          </label>
          <LaunchAtFields id="launch-at-input" value={form.launch_at_input} onChange={(v) => setForm((f) => ({ ...f, launch_at_input: v }))} />
          <div className="text-[11.5px] mt-2 leading-relaxed" style={{ color: 'var(--sc-text-mute)' }}>
            Type the month, day, year, hour and minute, then pick AM or PM. Leave every box empty for no launch date. Times are entered and displayed in
            America/New_York (EST/EDT).
            {form.launch_at_input === LAUNCH_AT_INVALID ? (
              <div className="mt-1" style={{ color: '#FF6B6B', fontWeight: 600 }} data-testid="launch-at-invalid">
                Enter a complete, real date and time.
              </div>
            ) : form.launch_at_input ? (
              <div className="mt-1" data-testid="launch-at-preview">
                Will display as: <span style={{ color: '#FFC20E', fontWeight: 600 }}>{easternDisplay(easternInputToIso(form.launch_at_input))}</span>
              </div>
            ) : null}
          </div>
        </div>

        {/* Heartbeat pacing */}
        <div>
          <label htmlFor="pacing-input" className="block text-[12px] font-bold uppercase tracking-[0.16em] mb-2" style={{ color: 'var(--sc-text-mute)' }}>
            Heartbeat Pacing Ratio
          </label>
          <div className="flex items-center gap-3 flex-wrap">
            <input
              id="pacing-input"
              type="number"
              step="0.01"
              min={PACING_MIN}
              max={PACING_MAX}
              value={form.heartbeat_pacing_ratio}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  heartbeat_pacing_ratio: e.target.value,
                }))
              }
              className="rounded-xl px-3 py-2.5 text-[14px] w-32 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FFC20E]/60"
              style={{
                background: 'rgba(0,0,0,0.34)',
                border: '1px solid rgba(255,255,255,0.12)',
                color: 'var(--sc-text)',
                fontVariantNumeric: 'tabular-nums',
              }}
              data-testid="pacing-input"
            />
            <div className="text-[12.5px]" style={{ color: 'var(--sc-text-mute)' }}>
              0 = paused · 0.10 = spec default · 1.00 = max throughput
            </div>
          </div>
          {Number(form.heartbeat_pacing_ratio) <= 0 ? (
            <div
              className="inline-flex items-center gap-2 mt-3 px-3 py-1.5 rounded-lg text-[11.5px] font-bold uppercase tracking-[0.16em]"
              style={{
                background: 'rgba(255, 99, 71, 0.10)',
                border: '1px solid rgba(255, 99, 71, 0.42)',
                color: '#FF6347',
              }}
              data-testid="pacing-paused-indicator"
            >
              <Pause className="w-3.5 h-3.5" />
              Engine paused — no cycles will run
            </div>
          ) : null}
        </div>

        {/* Save / Reset row */}
        <div className="flex items-center gap-2 pt-2">
          <button
            type="button"
            className="sc-btn-gold inline-flex items-center gap-2"
            style={{ padding: '10px 18px', fontSize: 12 }}
            disabled={!dirty || saving}
            onClick={handleSave}
            data-testid="launch-control-save-btn"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
          <button
            type="button"
            className="sc-btn-ghost inline-flex items-center gap-2"
            disabled={!dirty || saving}
            onClick={handleResetForm}
            data-testid="launch-control-reset-btn"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            Reset
          </button>
        </div>
      </div>

      {/* --- Refund All Purchases (LEGACY / HIDDEN per Patch v15) ------ */}
      {SHOW_LEGACY_REFUND_BUTTON ? (
        <div className="sc-glass rounded-2xl p-5 md:p-6" style={{ borderColor: 'rgba(255, 99, 71, 0.32)' }} data-testid="refund-all-panel">
          <div className="flex items-start gap-3 mb-3">
            <div
              className="inline-flex items-center justify-center w-9 h-9 rounded-lg"
              style={{
                background: 'rgba(255, 99, 71, 0.10)',
                border: '1px solid rgba(255, 99, 71, 0.42)',
              }}
            >
              <ShieldAlert className="w-4 h-4 text-[#FF6347]" />
            </div>
            <div>
              <h3 className="font-display text-[15px] font-bold">Refund All Purchases</h3>
              <p className="text-[12px] mt-1 leading-relaxed max-w-[640px]" style={{ color: 'var(--sc-text-mute)' }}>
                Refunds every active Sub Pack and every pending Single Position purchase back to each buyer&apos;s purchase balance, marks all Sub Packs
                inactive, and drains both pool queues. Cycle payouts already credited to withdrawal balances are NOT touched. Requires Pre-Launch Mode to be
                currently enabled.
              </p>
            </div>
          </div>

          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-xl px-4 py-2 text-[12.5px] font-bold"
            style={{
              background: 'rgba(255, 99, 71, 0.12)',
              border: '1px solid rgba(255, 99, 71, 0.55)',
              color: '#FF6347',
              cursor: server.pre_launch_mode_enabled ? 'pointer' : 'not-allowed',
              opacity: server.pre_launch_mode_enabled ? 1 : 0.42,
            }}
            disabled={!server.pre_launch_mode_enabled || refunding}
            onClick={() => {
              setConfirmText('');
              setRefundDialogOpen(true);
            }}
            data-testid="refund-all-btn"
          >
            <AlertTriangle className="w-3.5 h-3.5" />
            Refund All Purchases…
          </button>
          {!server.pre_launch_mode_enabled ? (
            <div className="text-[11.5px] mt-2" style={{ color: 'var(--sc-text-mute)' }} data-testid="refund-all-disabled-hint">
              Enable Pre-Launch Mode and Save first to unlock this action.
            </div>
          ) : null}

          {lastRefundResult ? (
            <div
              className="mt-4 rounded-xl p-3 text-[11.5px] leading-relaxed"
              style={{
                background: 'rgba(0,0,0,0.34)',
                border: '1px solid rgba(255,255,255,0.08)',
                color: 'var(--sc-text-mute)',
              }}
              data-testid="refund-last-result"
            >
              <div style={{ color: '#FFC20E', fontWeight: 600 }}>Last refund batch: {lastRefundResult.batch_id}</div>
              <div>
                Users affected: {lastRefundResult.users_affected} · Sub Packs refunded: {lastRefundResult.refunded_subpack_count} ($
                {lastRefundResult.refunded_subpack_total_usd}) · Positions refunded: {lastRefundResult.refunded_position_count} ($
                {lastRefundResult.refunded_position_total_usd})
              </div>
              {Array.isArray(lastRefundResult.errors) && lastRefundResult.errors.length ? (
                <div className="mt-1" style={{ color: '#FF6347' }}>
                  Errors: {lastRefundResult.errors.length} — see backend logs for details.
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      {/* --- Patch v15: Platform Unwind subpanel ------------------------- */}
      <PlatformUnwindPanel preLaunchEnabled={server.pre_launch_mode_enabled} />

      {/* --- Refund confirmation dialog --------------------------------- */}
      <AlertDialog open={refundDialogOpen} onOpenChange={setRefundDialogOpen}>
        <AlertDialogContent data-testid="refund-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Refund every purchase?</AlertDialogTitle>
            <AlertDialogDescription>
              This will refund every active Sub Pack and every pending Single Position purchase to each buyer&apos;s purchase balance. Active Sub Packs will be
              marked inactive and the pool queues drained. This action cannot be undone.
              <br />
              <br />
              Type <span style={{ color: '#FFC20E', fontWeight: 700 }}>REFUND ALL</span> below to confirm.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="Type: REFUND ALL"
            className="w-full rounded-xl px-3 py-2.5 text-[14px] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FFC20E]/60"
            style={{
              background: 'rgba(0,0,0,0.34)',
              border: '1px solid rgba(255,255,255,0.12)',
              color: 'var(--sc-text)',
              letterSpacing: '0.04em',
            }}
            data-testid="refund-confirm-input"
            // biome-ignore lint/a11y/noAutofocus: focus the confirmation field of the refund dialog the admin just opened
            autoFocus
          />
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setConfirmText('')} data-testid="refund-confirm-cancel">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={confirmText.trim() !== 'REFUND ALL' || refunding}
              onClick={(e) => {
                e.preventDefault();
                handleRefund();
              }}
              data-testid="refund-confirm-execute"
            >
              {refunding ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-2" /> : null}
              {refunding ? 'Refunding...' : 'Refund Everyone'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------
// The eligible-member count, or null when the server could not count it.
const eligibleCount = (value) => (Number.isFinite(value) ? value : null);

const StatusTile = ({ icon: Icon, label, value, tone = 'neutral', testId }) => {
  const toneColor = tone === 'warn' ? '#FF6347' : tone === 'ok' ? '#5DD9A6' : '#FFC20E';
  return (
    <div
      className="rounded-xl px-4 py-3"
      style={{
        background: 'rgba(0,0,0,0.34)',
        border: '1px solid rgba(255,255,255,0.08)',
      }}
      data-testid={testId}
    >
      <div className="flex items-center gap-2 mb-1.5">
        <Icon className="w-3.5 h-3.5" style={{ color: toneColor }} />
        <div className="text-[10.5px] uppercase tracking-[0.16em] font-bold" style={{ color: 'var(--sc-text-mute)' }}>
          {label}
        </div>
      </div>
      <div
        className="font-display text-[14px] md:text-[15px] font-bold leading-tight"
        style={{ color: toneColor, fontVariantNumeric: 'tabular-nums' }}
        data-testid={`${testId}-value`}
      >
        {value}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Platform Unwind Subpanel (Patch v15)
// ---------------------------------------------------------------------------
// Houses two god-admin-only buttons:
//   1) Refund Net Crypto Deposits
//      Replaces each user's purchase_balance with max(0, total_deposits -
//      total_completed_withdrawals).
//   2) Clear All Withdrawal Balances
//      Zeroes every user's withdrawal_balance > 0.
//
// Both buttons follow the same pattern:
//   * Click "Preview…" → fetches a server-computed dry-run, shows total $,
//     affected user count, and top 10 by amount.
//   * Operator types the EXACT dollar amount as the confirm phrase.
//   * Click "Execute" → server re-runs the math and refuses if the typed
//     amount no longer matches the live total (drift protection).
// ---------------------------------------------------------------------------
const PlatformUnwindPanel = ({ preLaunchEnabled }) => {
  return (
    <div className="sc-glass rounded-2xl p-5 md:p-6 space-y-5" style={{ borderColor: 'rgba(255, 99, 71, 0.42)' }} data-testid="platform-unwind-panel">
      <div className="flex items-start gap-3">
        <div
          className="inline-flex items-center justify-center w-10 h-10 rounded-xl"
          style={{
            background: 'rgba(255, 99, 71, 0.12)',
            border: '1px solid rgba(255, 99, 71, 0.55)',
          }}
        >
          <ShieldAlert className="w-5 h-5 text-[#FF6347]" />
        </div>
        <div>
          <h3 className="font-display text-[16px] font-bold uppercase tracking-[0.14em]" style={{ color: '#FF6347' }} data-testid="platform-unwind-title">
            Platform Unwind · Irreversible Actions
          </h3>
          <p className="text-[12.5px] mt-1.5 leading-relaxed max-w-[680px]" style={{ color: 'var(--sc-text-mute)' }}>
            God-admin-only sweeps for fully unwinding the platform&apos;s financial state. Each action is gated by Pre-Launch Mode and a typed dollar-amount
            confirmation that the server re-verifies at execute time. Every per-user change writes a Ledger entry for full auditability.
          </p>
        </div>
      </div>

      <UnwindAction
        kind="refund"
        title="Refund Net Crypto Deposits"
        icon={Banknote}
        description={
          <>
            For each user, sets <strong>purchase_balance</strong> = max(0, total deposits − completed withdrawals). Uses the unified Ledger so both legacy and
            v2 deposit flows are captured automatically. Replace semantics: existing purchase_balance is overwritten.
          </>
        }
        previewFn={fetchPlatformUnwindRefundPreview}
        executeFn={executePlatformUnwindRefund}
        amountField="total_refund_usd"
        countLabel="users will be touched"
        tableHeaders={['User', 'Deposits', 'Withdrawn', 'Current Purchase', 'Net Refund', 'Delta']}
        rowRenderer={(r) => [
          r.username || r.user_id.slice(0, 8),
          fmtUsd(r.total_deposits),
          fmtUsd(r.total_withdrawn),
          fmtUsd(r.current_purchase_balance),
          fmtUsd(r.net_refund),
          `${Number(r.delta) >= 0 ? '+' : '−'}${fmtUsd(Math.abs(Number(r.delta)))}`,
        ]}
        preLaunchEnabled={preLaunchEnabled}
        testIdPrefix="unwind-refund"
      />

      <UnwindAction
        kind="clear"
        title="Clear All Withdrawal Balances"
        icon={Eraser}
        description={
          <>
            Zeroes every user&apos;s <strong>withdrawal_balance</strong> that is currently &gt; 0. Wipes earned matrix profits across the platform. Use only
            during a controlled platform-wide reset or relaunch.
          </>
        }
        previewFn={fetchPlatformUnwindClearPreview}
        executeFn={executePlatformUnwindClear}
        amountField="total_clear_usd"
        countLabel="users will be cleared"
        tableHeaders={['User', 'Current Withdrawal Balance', 'Amount Cleared']}
        rowRenderer={(r) => [r.username || r.user_id.slice(0, 8), fmtUsd(r.current_withdrawal_balance), fmtUsd(r.amount_to_clear)]}
        preLaunchEnabled={preLaunchEnabled}
        testIdPrefix="unwind-clear"
      />

      {/* Patch v15.1 — Reset Matrix State action ----------------------- */}
      <UnwindAction
        kind="matrix"
        title="Reset Matrix State"
        icon={Cpu}
        description={
          <>
            Drains both position queues, wipes the cycle archive (rotator_pool), deactivates active Sub Packs, resets every user&apos;s lifetime cycle counters,
            and zeroes the global Redis cycle metric. Touches <strong>zero balances</strong> &mdash; this is matrix-state cleanup only. Use as the final step of
            a relaunch sequence, after the two refund actions above.
          </>
        }
        previewFn={fetchPlatformUnwindResetMatrixPreview}
        executeFn={executePlatformUnwindResetMatrix}
        amountField="queue_entries_total"
        countLabel="users will have cycle counters reset"
        tableHeaders={['User', 'Own Cycles', 'Referral Cycles', 'Total Lifetime Cycles']}
        rowRenderer={(r) => [
          r.username || r.user_id?.slice(0, 8) || '',
          r.own_cycle_count?.toLocaleString?.() ?? r.own_cycle_count ?? 0,
          r.referral_cycle_count?.toLocaleString?.() ?? r.referral_cycle_count ?? 0,
          r.total_lifetime_cycles?.toLocaleString?.() ?? r.total_lifetime_cycles ?? 0,
        ]}
        preLaunchEnabled={preLaunchEnabled}
        testIdPrefix="unwind-matrix"
        // Count-based confirm overrides (Patch v15.1):
        amountPrefix=""
        amountLabel="Queue entries to drain"
        displayTotalField="display_total"
        amountSubtitleField="display_subtitle"
        confirmLabel="count"
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// UnwindAction — shared preview+execute UI for both unwind buttons.
// ---------------------------------------------------------------------------
const UnwindAction = ({
  kind,
  title,
  icon: Icon,
  description,
  previewFn,
  executeFn,
  amountField,
  countLabel,
  tableHeaders,
  rowRenderer,
  preLaunchEnabled,
  testIdPrefix,
  // Patch v15.1 — optional overrides for the Matrix Reset action which
  // displays a count instead of a dollar amount. All have backward-
  // compatible defaults so the existing two actions are unchanged.
  amountPrefix = '$',
  amountLabel = 'Total amount',
  displayTotalField = null,
  amountSubtitleField = null,
  confirmLabel = 'amount',
}) => {
  const [previewing, setPreviewing] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [confirmText, setConfirmText] = useState('');
  const [lastResult, setLastResult] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const expectedAmount = previewData?.confirm_phrase || '';
  const usersAffected = previewData?.users_affected || 0;
  // Big-number display: prefer the dedicated display field when provided
  // (Matrix Reset uses display_total = "196,841"); otherwise fall back to
  // the legacy dollar-amount field (refund/clear actions).
  const totalUsd = (displayTotalField && previewData?.[displayTotalField]) || previewData?.[amountField] || (displayTotalField ? '0' : '0.00');
  const subtitleText = amountSubtitleField ? previewData?.[amountSubtitleField] || '' : '';

  const onPreview = async () => {
    setPreviewing(true);
    try {
      const data = await previewFn();
      setPreviewData(data);
      setConfirmText('');
      setDialogOpen(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'object' ? detail?.message || detail?.code : detail || err.message;
      toast.error(`Preview failed: ${msg}`);
    } finally {
      setPreviewing(false);
    }
  };

  const onRefreshPreview = async () => {
    setPreviewing(true);
    try {
      const data = await previewFn();
      setPreviewData(data);
      setConfirmText('');
      toast.success('Preview refreshed.');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'object' ? detail?.message || detail?.code : detail || err.message;
      toast.error(`Refresh failed: ${msg}`);
    } finally {
      setPreviewing(false);
    }
  };

  const onExecute = async () => {
    setExecuting(true);
    try {
      const data = await executeFn(confirmText.trim());
      if (data?.status === 'confirm_mismatch') {
        toast.error(`Total drifted. Expected: $${data.expected_confirm_phrase}. Refresh the preview and try again.`);
        // Auto-refresh the preview so the operator sees the new total.
        await onRefreshPreview();
        return;
      }
      setLastResult(data);
      toast.success(`${title} complete: ${data.users_affected} users.`);
      setDialogOpen(false);
      setConfirmText('');
      setPreviewData(null);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'object' ? detail?.message || detail?.code : detail || err.message;
      toast.error(`Execute failed: ${msg}`);
    } finally {
      setExecuting(false);
    }
  };

  return (
    <div
      className="rounded-xl p-4 md:p-5"
      style={{
        background: 'rgba(0,0,0,0.34)',
        border: '1px solid rgba(255,99,71,0.18)',
      }}
      data-testid={`${testIdPrefix}-section`}
    >
      <div className="flex items-start gap-3">
        <div
          className="inline-flex items-center justify-center w-9 h-9 rounded-lg"
          style={{
            background: 'rgba(255, 99, 71, 0.10)',
            border: '1px solid rgba(255, 99, 71, 0.42)',
          }}
        >
          <Icon className="w-4 h-4 text-[#FF6347]" />
        </div>
        <div className="flex-1">
          <h4 className="font-display text-[14.5px] font-bold">{title}</h4>
          <p className="text-[12px] mt-1 leading-relaxed max-w-[640px]" style={{ color: 'var(--sc-text-mute)' }}>
            {description}
          </p>
        </div>
      </div>

      <div className="mt-4 flex items-center gap-2 flex-wrap">
        <button
          type="button"
          className="inline-flex items-center gap-2 rounded-xl px-4 py-2 text-[12.5px] font-bold"
          style={{
            background: 'rgba(255, 99, 71, 0.12)',
            border: '1px solid rgba(255, 99, 71, 0.55)',
            color: '#FF6347',
            cursor: preLaunchEnabled ? 'pointer' : 'not-allowed',
            opacity: preLaunchEnabled ? 1 : 0.42,
          }}
          disabled={!preLaunchEnabled || previewing}
          onClick={onPreview}
          data-testid={`${testIdPrefix}-preview-btn`}
        >
          {previewing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <AlertTriangle className="w-3.5 h-3.5" />}
          {previewing ? 'Loading…' : 'Preview…'}
        </button>
        {!preLaunchEnabled ? (
          <span className="text-[11.5px]" style={{ color: 'var(--sc-text-mute)' }} data-testid={`${testIdPrefix}-disabled-hint`}>
            Enable Pre-Launch Mode above to unlock.
          </span>
        ) : null}
      </div>

      {lastResult ? (
        <div
          className="mt-4 rounded-xl p-3 text-[11.5px] leading-relaxed"
          style={{
            background: 'rgba(0,0,0,0.34)',
            border: '1px solid rgba(255,255,255,0.08)',
            color: 'var(--sc-text-mute)',
          }}
          data-testid={`${testIdPrefix}-last-result`}
        >
          <div style={{ color: '#FFC20E', fontWeight: 600 }}>Last batch: {lastResult.batch_id}</div>
          <div className="mt-0.5">
            Users affected: <strong>{lastResult.users_affected}</strong>
            {kind === 'refund' ? (
              <>
                {' '}
                · Credited: {fmtUsd(lastResult.total_credited_usd)} · Debited: {fmtUsd(lastResult.total_debited_usd)}
              </>
            ) : kind === 'clear' ? (
              <> · Total cleared: {fmtUsd(lastResult.total_cleared_usd)}</>
            ) : kind === 'matrix' ? (
              <>
                {' '}
                · Sub Packs deactivated: <strong>{lastResult.subpacks_deactivated_count ?? 0}</strong> · User counters reset:{' '}
                <strong>{lastResult.user_counters_reset_count ?? 0}</strong>
              </>
            ) : null}
          </div>
          {kind === 'matrix' && lastResult.inner_reset_response ? (
            <div className="mt-1.5 text-[11px] sc-tabular" style={{ color: 'var(--sc-text-dim)' }} data-testid={`${testIdPrefix}-inner-summary`}>
              Queue drained: {lastResult.inner_reset_response.deleted?.queue_mongo_total ?? 0}
              {' · '}Cycle archive wiped: {lastResult.inner_reset_response.deleted?.rotator_pool_archived ?? 0}
              {' · '}Redis lists flushed:{' '}
              {(lastResult.inner_reset_response.deleted?.redis_root_entries_flushed ?? 0) +
                (lastResult.inner_reset_response.deleted?.redis_filler_entries_flushed ?? 0)}
            </div>
          ) : null}
          {Array.isArray(lastResult.errors) && lastResult.errors.length ? (
            <div className="mt-1" style={{ color: '#FF6347' }}>
              Errors: {lastResult.errors.length} — see backend logs for details.
            </div>
          ) : null}
        </div>
      ) : null}

      <AlertDialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <AlertDialogContent className="max-w-[640px]" data-testid={`${testIdPrefix}-confirm-dialog`}>
          <AlertDialogHeader>
            <AlertDialogTitle>{title} · Preview</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <div
                  className="rounded-xl p-3"
                  style={{
                    background: 'rgba(0,0,0,0.34)',
                    border: '1px solid rgba(255,182,18,0.42)',
                  }}
                >
                  <div className="text-[11px] uppercase tracking-[0.18em] font-bold" style={{ color: 'var(--sc-text-mute)' }}>
                    {amountLabel}
                  </div>
                  <div className="font-display text-[26px] font-bold sc-tabular" style={{ color: '#FFC20E' }} data-testid={`${testIdPrefix}-preview-total`}>
                    {amountPrefix}
                    {totalUsd}
                  </div>
                  {subtitleText ? (
                    <div
                      className="text-[11.5px] mt-1 leading-relaxed"
                      style={{ color: 'var(--sc-text-dim)' }}
                      data-testid={`${testIdPrefix}-preview-subtitle`}
                    >
                      {subtitleText}
                    </div>
                  ) : null}
                  <div className="text-[12px] mt-0.5" style={{ color: 'var(--sc-text-mute)' }}>
                    <strong>{usersAffected}</strong> {countLabel}
                  </div>
                </div>

                {previewData?.top_users?.length ? (
                  <div className="rounded-xl overflow-hidden text-[11.5px]" style={{ border: '1px solid rgba(255,255,255,0.08)' }}>
                    <div
                      className="px-3 py-2 font-bold text-[11px] uppercase tracking-[0.16em]"
                      style={{
                        background: 'rgba(255,255,255,0.04)',
                        color: 'var(--sc-text-mute)',
                      }}
                    >
                      Top {previewData.top_users.length} by {confirmLabel}
                    </div>
                    <div className="overflow-auto max-h-[260px]">
                      <table className="w-full text-left" data-testid={`${testIdPrefix}-preview-table`}>
                        <thead>
                          <tr style={{ color: 'var(--sc-text-mute)' }}>
                            {tableHeaders.map((h) => (
                              <th
                                key={h}
                                className="px-3 py-2 text-[10.5px] uppercase tracking-[0.14em] font-bold"
                                style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}
                              >
                                {h}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {previewData.top_users.map((r) => (
                            <tr key={r.user_id} style={{ borderTop: '1px solid rgba(255,255,255,0.04)' }}>
                              {rowRenderer(r).map((cell, i) => (
                                <td key={i} className="px-3 py-2 sc-tabular" style={{ color: 'var(--sc-text)' }}>
                                  {cell}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <div className="text-[12px]" style={{ color: 'var(--sc-text-mute)' }}>
                    No users to display.
                  </div>
                )}

                <div className="pt-1">
                  <div className="text-[12px] leading-relaxed" style={{ color: 'var(--sc-text-dim)' }}>
                    To confirm, type the {confirmLabel}{' '}
                    <span className="sc-tabular" style={{ color: '#FFC20E', fontWeight: 700 }}>
                      {expectedAmount}
                    </span>{' '}
                    below. The server re-runs the math at execute time and rejects if the total has drifted since this preview.
                  </div>
                  <input
                    type="text"
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    placeholder={`Type: ${expectedAmount}`}
                    className="w-full mt-2 rounded-xl px-3 py-2.5 text-[14px] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FFC20E]/60 sc-tabular"
                    style={{
                      background: 'rgba(0,0,0,0.34)',
                      border: '1px solid rgba(255,255,255,0.12)',
                      color: 'var(--sc-text)',
                      letterSpacing: '0.04em',
                    }}
                    data-testid={`${testIdPrefix}-confirm-input`}
                    // biome-ignore lint/a11y/noAutofocus: focus the confirmation field of the unwind dialog the admin just opened
                    autoFocus
                  />
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <button
              type="button"
              className="inline-flex items-center gap-2 rounded-xl px-3 py-2 text-[12px] font-bold"
              style={{
                background: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.12)',
                color: 'var(--sc-text)',
              }}
              onClick={onRefreshPreview}
              disabled={previewing || executing}
              data-testid={`${testIdPrefix}-refresh-btn`}
            >
              <RefreshCw className={`w-3.5 h-3.5 ${previewing ? 'animate-spin' : ''}`} />
              Refresh Preview
            </button>
            <AlertDialogCancel
              onClick={() => {
                setConfirmText('');
              }}
              data-testid={`${testIdPrefix}-cancel-btn`}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={confirmText.trim() !== expectedAmount || executing || !expectedAmount}
              onClick={(e) => {
                e.preventDefault();
                onExecute();
              }}
              data-testid={`${testIdPrefix}-execute-btn`}
            >
              {executing ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-2" /> : null}
              {executing ? 'Executing…' : 'Execute'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default LaunchControlSubpage;
